import { useEffect } from "react";
import { Check, X } from "lucide-react";
import {
  AREA_COLORS,
  DEFAULT_AREA_COLOR,
  type ListColorKey,
} from "@/lib/listColors";

export type ListOption = {
  id: number;
  name: string;
  kind: "inbox" | "custom";
  color?: string | null;
  bgColor?: string | null;
  categoryId?: number | null;
  categoryName?: string | null;
};

export type ListSelectorProps = {
  lists: ListOption[];
  value: number | null;
  onChange: (listId: number | null) => void;
  onClose: () => void;
  title?: string;
};

/**
 * Generic list selection modal. It only presents options and reports the
 * selection; callers decide whether the value belongs to a task, pattern,
 * filter, or another feature.
 */
export default function ListSelector({
  lists,
  value,
  onChange,
  onClose,
  title = "Select list",
}: ListSelectorProps) {
  const inboxList = lists.find(list => list.kind === "inbox");

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleSelect = (listId: number | null) => {
    if (listId === value) {
      onClose();
      return;
    }
    onChange(listId);
  };

  return (
    <>
      <div className="fixed inset-0 z-[59] bg-black/40" onClick={onClose} />
      <div className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none px-4">
        <div
          className="pointer-events-auto w-full max-w-xs space-y-3 rounded-2xl border border-border bg-background p-4 shadow-2xl"
          onClick={event => event.stopPropagation()}
        >
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-foreground">
              {title}
            </h3>
            <button
              type="button"
              onClick={() => {
                if (inboxList) handleSelect(inboxList.id);
                else onClose();
              }}
              disabled={!inboxList || value === inboxList?.id}
              className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground ${!inboxList || value === inboxList?.id ? "invisible" : ""}`}
              aria-label="Select Inbox"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="max-h-96 space-y-1 overflow-y-auto">
            {lists.length === 0 ? (
              <p className="px-3 py-4 text-center text-sm text-muted-foreground">
                No lists available.
              </p>
            ) : (
              lists.map(list => {
                const listColors =
                  AREA_COLORS[list.color as ListColorKey] ??
                  AREA_COLORS[DEFAULT_AREA_COLOR];
                const active = list.id === value;
                return (
                  <button
                    key={list.id}
                    type="button"
                    onClick={() => handleSelect(list.id)}
                    className={`inline-flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${active ? "bg-accent/50 font-semibold" : "hover:bg-accent/30"}`}
                  >
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ background: listColors.base }}
                      aria-hidden="true"
                    />
                    <span className="flex-1 truncate">{list.name}</span>
                    {active && (
                      <Check className="h-3.5 w-3.5 text-foreground" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export { ListSelector };
